import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Info, Pencil, Search, CalendarPlus } from "lucide-react";

import { Customer } from "models/customer/customer";
import { CustomerSearchParam } from "models/customer/customer-search-param";
import { GetCustomers } from "api/customer/index";

import LoadingComponent from "common/components/loader";
import PagedTableContainer from "common/paged-table-container";
import SearchNotFoundComponent from "common/components/search-not-found";
import { RoutePath } from "routes/route-path";

interface Props {
  reload: number;
  onCustomerEditClick: (customer: Customer) => void;
}

const SearchCustomerComponent: React.FC<Props> = ({ reload, onCustomerEditClick }) => {
  const navigate = useNavigate();

  const [data, setData] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  // const [searchParam, setSearchParam] = useState<VehicleSearchParam>({});
  // const [selectedVehicle, setSelectedVehicle] = useState<Vehicle>();

  /*  useEffect(() => {
    console.log("reload");
    onSearchClick();
  }, [reload]);

   useEffect(() => {
    console.log("initial");
    loadCustomers({});
  }, []);
 */
  useEffect(() => {
    if (currentPage) {
      console.log("page:" + currentPage);
      loadCustomers({
        searchText: searchText,
      });
    }
  }, [currentPage]);

  const onSearchClick = async () => {
    const _param = {
      searchText: searchText,
    };
    setCurrentPage(1);
    await loadCustomers(_param);
  };
  const loadCustomers = async (param: CustomerSearchParam) => {
    setLoading(true);

    const res = await GetCustomers(param, undefined, pageSize, currentPage);
    // console.log(res);
    if (!(res?.status?.isError ?? false) && res?.data) {
      setData(res?.data ?? []);
      setTotal(res?.totalResultCount ?? 0);
    } else {
      setData([]);
      setTotal(0);
    }
    setLoading(false);
  };
  const handleEditClick = (customer: Customer) => {
    if (onCustomerEditClick != null) {
      onCustomerEditClick(customer);
    }
  };
  const handleCreateJobClick = (customer: Customer) => {
    navigate(RoutePath.Admin_Job_create + "?c=" + customer.id);
  };

  const columns = useMemo(
    () => [
      {
        header: "Customer",
        accessorKey: "contactNumber",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <>
            <p>Customer Name: {cell.row.original.name ?? ""}</p>
            <p>Contact No: {cell.row.original.contactNumber ?? ""}</p>
            <p>Email: {cell.row.original.email ?? ""}</p>
          </>
        ),
      },
      {
        header: "Vehicle No",
        accessorKey: "vehicleNo",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <>
            {(cell.row.original.vehicles ?? []).map((vehicle: any, index: number) => (
              <div key={index} className="vehicle-details">
                <p>Vehicle No: {vehicle.vehicleNo}</p>
                <p>Chassis No: {vehicle.chassisNo}</p>
                <p>Year: {vehicle.year}</p>
              </div>
            ))}
          </>

          /*   <>
            <p>Vehicle No: {cell.row.original.vehicleNo}</p>
            <p>Chassis No: {cell.row.original.chassisNo}</p>
            <p>Year: {cell.row.original.year}</p>
          </> */
        ),
      },

      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <div className="flex justify-end gap-2">
            <div
              className="flex items-center justify-center px-4 py-1 size-y-8 transition-all duration-200 ease-linear rounded-md text-slate-500 bg-slate-100 hover:text-white hover:bg-slate-500 dark:text-zink-200 dark:bg-zink-600 dark:hover:text-white dark:hover:bg-zink-400 cursor-pointer"
              onClick={() => handleCreateJobClick(cell.row.original)}
            >
              <CalendarPlus className="size-4" /> <span className="px-2">Create a Job</span>
            </div>
            <Link
              to="#!"
              onClick={() => handleEditClick(cell.row.original)}
              data-modal-target="leaveOverviewModal"
              className="flex items-center justify-center px-4 py-1 size-y-8 transition-all duration-200 ease-linear rounded-md text-custom-500 bg-custom-100 hover:text-white hover:bg-custom-500 dark:bg-custom-500/20 dark:hover:bg-custom-500"
            >
              <Pencil className="size-4" /> <span className="px-2">Edit</span>
            </Link>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-5">
      <div className="xl:col-span-12">
        <div className="card">
          <div className="card-body">
            <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2 xl:grid-cols-12">
              <div className="xl:col-span-10">
                <div className="relative">
                  <input
                    type="text"
                    className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="Search by Customer Name Email Phone"
                    autoComplete="off"
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                </div>
              </div>
              <div className="xl:col-span-2">
                <div className="ltr:lg:text-right rtl:lg:text-left">
                  <Link
                    to=""
                    onClick={onSearchClick}
                    type="button"
                    className="text-white btn  min-w-40 bg-custom-800 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                  >
                    <span className="align-middle">Search</span>
                  </Link>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-12">
              <div className="xl:col-span-12">
                {loading ? (
                  <LoadingComponent></LoadingComponent>
                ) : (
                  <div className="overflow-x-auto mt-2">
                    {data && data.length > 0 ? (
                      <PagedTableContainer
                        isPagination={true}
                        columns={columns || []}
                        pagedData={data || []}
                        totalCount={total}
                        currentPage={currentPage}
                        customPageSize={pageSize}
                        divclassName="overflow-x-auto"
                        tableclassName="w-full whitespace-nowrap"
                        theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:bg-zink-600 dark:text-zink-200"
                        thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                        tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                        PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                        onPageChanged={(pageIndex: number) => {
                          setCurrentPage(pageIndex + 1);
                        }}
                      />
                    ) : (
                      <SearchNotFoundComponent title="Sorry! No Result Found">
                        <p className="mb-0 text-slate-500 dark:text-zink-200">
                          We've searched customers matched to your criteria, We did not find any.
                        </p>
                      </SearchNotFoundComponent>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchCustomerComponent;
